import AuthPageGuard from "components/AuthPageGuard";
import TwoFactorSetupWizard from "components/twofactor/TwoFactorSetupWizard";
import { Box, Button, Card, Heading, Input, Text, VStack } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "services/authService";
import { getThemeColor } from "theme/colors";

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  requirements: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
  };
}

const calculatePasswordStrength = (password: string): PasswordStrength => {
  const requirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    // eslint-disable-next-line no-useless-escape
    hasSpecial: /[!@#$%^&*()_+={};\[\]':"\\|,.<>/?]/.test(password),
  };

  let score = 0;
  if (requirements.minLength) score++;
  if (requirements.hasUppercase) score++;
  if (requirements.hasLowercase) score++;
  if (requirements.hasNumber) score++;
  if (requirements.hasSpecial) score++;

  const labels = ["Very Weak", "Weak", "Fair", "Good", "Strong"];
  const colors = ["red", "orange", "yellow", "blue", "green"];

  return {
    score,
    label: labels[Math.min(score, 4)],
    color: colors[Math.min(score, 4)],
    requirements,
  };
};

export default function AcceptInvitation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordStrength, setPasswordStrength] =
    useState<PasswordStrength | null>(null);
  const [token, setToken] = useState<string>("");
  // Set when the inviting company requires 2FA: the user must enroll before reaching the app.
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    if (!tokenParam) {
      setError(t("app.auth.acceptInvitation.invalidLink"));
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, t]);

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (value) {
      setPasswordStrength(calculatePasswordStrength(value));
    } else {
      setPasswordStrength(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError(t("app.auth.acceptInvitation.invalidLinkShort"));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("app.auth.signUp.passwordsDoNotMatch"));
      return;
    }

    if (password.length < 8) {
      setError(t("app.auth.signUp.passwordTooShort"));
      return;
    }

    if (!passwordStrength || passwordStrength.score < 3) {
      setError(t("app.auth.signUp.passwordTooWeak"));
      return;
    }

    setLoading(true);
    try {
      const result = await authService.acceptInvitation({
        token,
        password,
      });

      if (result.mfaRequired && result.mfaToken) {
        setMfaToken(result.mfaToken);
      } else {
        navigate("/admin");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          t("app.auth.acceptInvitation.acceptFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageGuard>
      <Box w="full">
        <VStack gap={8} align="stretch">
          {/* Header */}
          <VStack gap={2} align="start">
            <Heading
              size="2xl"
              color={getThemeColor("text.primary")}
              fontWeight="bold"
            >
              {t("app.auth.acceptInvitation.title")}
            </Heading>
            <Text fontSize="md" color={getThemeColor("text.secondary")}>
              {t("app.auth.acceptInvitation.subtitle")}
            </Text>
          </VStack>

          {/* Error Message */}
          {error && (
            <Box
              bg={getThemeColor("alert.error.bg")}
              border="1px solid"
              borderColor={getThemeColor("alert.error.border")}
              borderRadius="lg"
              p={3}
            >
              <Text
                fontSize="sm"
                color={getThemeColor("alert.error.text")}
                fontWeight="medium"
              >
                {error}
              </Text>
            </Box>
          )}

          {/* Forced 2FA enrollment (when the inviting company requires it) */}
          {mfaToken && (
            <Card.Root>
              <Card.Body p={8}>
                <TwoFactorSetupWizard
                  onStart={() => authService.start2faLoginSetup(mfaToken)}
                  onVerify={async (code) => {
                    await authService.complete2faLoginSetup(mfaToken, code);
                  }}
                  onComplete={() => navigate("/admin")}
                />
              </Card.Body>
            </Card.Root>
          )}

          {/* Form Card */}
          {token && !mfaToken && (
            <Card.Root>
              <Card.Body p={8}>
                <form onSubmit={handleSubmit}>
                  <VStack gap={5} align="stretch">
                    {/* Password Field */}
                    <Box>
                      <Input
                        id="password"
                        type="password"
                        label={t("app.auth.signUp.passwordLabel")}
                        placeholder={t("app.auth.signUp.passwordPlaceholder")}
                        size="lg"
                        value={password}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        required
                      />
                      {/* Password Strength Indicator */}
                      {passwordStrength && (
                        <Box mt={2}>
                          <Box display="flex" gap={1} mb={2}>
                            {[1, 2, 3, 4, 5].map((level) => (
                              <Box
                                key={level}
                                flex={1}
                                h="4px"
                                bg={
                                  level <= passwordStrength.score
                                    ? passwordStrength.color === "red"
                                      ? "red.500"
                                      : passwordStrength.color === "orange"
                                        ? "orange.500"
                                        : passwordStrength.color === "yellow"
                                          ? "yellow.500"
                                          : "green.500"
                                    : getThemeColor("border.default").base
                                }
                                borderRadius="full"
                              />
                            ))}
                          </Box>
                          <Text
                            fontSize="xs"
                            fontWeight="medium"
                            color={
                              passwordStrength.color === "red"
                                ? "red.500"
                                : passwordStrength.color === "orange"
                                  ? "orange.500"
                                  : passwordStrength.color === "yellow"
                                    ? "yellow.600"
                                    : "green.600"
                            }
                          >
                            {t(
                              `app.auth.signUp.passwordStrength.${passwordStrength.label.toLowerCase().replace(" ", "")}`,
                            )}
                          </Text>
                        </Box>
                      )}
                    </Box>

                    {/* Confirm Password Field */}
                    <Input
                      id="confirmPassword"
                      type="password"
                      label={t("app.auth.signUp.confirmPasswordLabel")}
                      placeholder={t(
                        "app.auth.signUp.confirmPasswordPlaceholder",
                      )}
                      size="lg"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={loading}
                      variant="solid"
                      colorPalette="blue"
                      size="lg"
                      w="full"
                      mt={2}
                    >
                      {loading
                        ? t("app.auth.signUp.creatingAccount")
                        : t("app.auth.acceptInvitation.submit")}
                    </Button>
                  </VStack>
                </form>
              </Card.Body>
            </Card.Root>
          )}
        </VStack>
      </Box>
    </AuthPageGuard>
  );
}
